import CloseButton from "@/design-system/SpecificButtons/CloseButton/CloseButton";
import { ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * The "you did it" moment: a green panel over a dimmed page, with an icon, a
 * title and a line under it, whatever the moment needs to show, and the one
 * action left. Streamline shows it when an SBC is done; My Club when its
 * onboarding is.
 *
 * Clicking the backdrop or the close button puts it away. It fades and
 * settles in with the backdrop: `starting:` is the entry state the
 * transition runs from on first paint.
 */
export const CelebrationDialog = ({
  isOpen,
  onClose,
  icon,
  title,
  subtitle,
  children,
  action,
}: {
  isOpen: boolean;
  onClose: () => void;
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Between the title and the action: figures, tips. */
  children?: ReactNode;
  action?: ReactNode;
}) =>
  isOpen
    ? createPortal(
        <div
          className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto bg-black/60 px-4 py-8 backdrop-blur-[2px] transition-opacity duration-200 starting:opacity-0"
          onClick={onClose}
        >
          <div
            role="dialog"
            aria-modal
            className="w-full transition-[opacity,transform] duration-200 ease-out starting:scale-95 starting:opacity-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative mx-auto flex max-w-130 flex-col items-center gap-4 rounded-2xl border border-streamline-current-border bg-streamline-current-surface px-6 py-8 text-center">
              <div className="absolute right-3 top-3">
                <CloseButton onClick={onClose} />
              </div>
              {icon}
              <div className="flex flex-col gap-1">
                <span className="text-2xl font-extrabold text-white">
                  {title}
                </span>
                {subtitle && (
                  <span className="text-sm font-semibold text-neutral-500">
                    {subtitle}
                  </span>
                )}
              </div>
              {children}
              {action}
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;
